/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-console */
import {
  waitForOneTrustReady,
  hasGTMConsentUpdate,
  queueGA4Event,
  getPendingGA4Events,
} from './GTM_polling';
import { addSeconds } from 'date-fns/addSeconds';
import { isAfter } from 'date-fns/isAfter';
import { parseISO } from 'date-fns/parseISO';
import { getCookie, setCookie } from 'cookies-next';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import axios from 'axios';
import { devLog } from './devLog';

dayjs.extend(utc);

// CHECKIN
// functions invented: pendo.track (not present but called)

export type SystemType = 'keen' | 'pendo' | 'gtm' | 'internal';

// LOGGED IN TRACKING MODULES
// FOR LOGGED OUT, REFER page/landing.js
const rates = {
  hour: 3600,
  halfDay: 43200,
  day: 86400,
};

type FrequencyType = keyof typeof rates;

export type Options = {
  frequency?: FrequencyType;
  maxTries?: number;
  interval?: number;
};

export type Property = {
  status?: number;
  type?: string;
  start_date?: string;
  platform?: string;
  loggedOut?: string;
  embedURL?: string;
  embedDomain?: string;
};

export type InternalProperties = {
  nodeID: number;
};

type MetaData = {
  platform: string;
  deviceID: string;
};

export type Event = {
  action: string;
  event?: string;
  properties?: Property;
  metadata?: MetaData;
};

export type Attributes = {
  eventName: string;
  meta: Property;
};

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
    pendo?: { track?: (...args: unknown[]) => void };
  }
}

/**.
 * Tracking Map.
 *
 * @param system {string} - Systems to track.
 * @param event {string} - description
 * @param properties {{}} =
 * @param options - Frequency data,etc.
 */
const trackingMap = (
  system: SystemType,
  event: string,
  properties: Property,
  options: Options,
) => {
  const map = {
    keen: () =>
      trackKeen(event.toLowerCase().split(' ').join('_'), properties, options),
    pendo: () => pendoAnalytics(event, properties, options),
    gtm: () => trackGTM(event, properties, options),
    internal: () =>
      trackInternal(event, properties as InternalProperties, options),
  };

  return map[system];
};

/**.
 * Calculate frequency checker
 *
 * @param systems {string[]} - Systems to track.
 * @param options - Frequency data,etc.
 */
const frequencyChecker = (
  event: string,
  systems: SystemType[],
  options: Options,
) => {
  const { frequency } = options;
  if (frequency) {
    const parsedRate =
      typeof frequency === 'string' ? rates[frequency] : frequency;

    // filter out systems that don't meet frequency rate
    return systems.filter((system) => {
      const lastFired = localStorage.getItem(`${system}:${event}`);

      if (!lastFired) {
        return true; // If never fired then fire now.
      }

      const lastFiredDate = parseISO(lastFired);
      // current time + frequency
      const nextScheduledFire = addSeconds(lastFiredDate, parsedRate);

      // return if lastFired > current-time + frequency
      return lastFired ? isAfter(new Date(), nextScheduledFire) : true;
    });
  } else {
    return systems;
  }
};

/**
 * Tracking function.
 * @param event - Event type.
 * @param properties - Accumulated through the work flow.
 * @param systems - Systems to be tracked.
 * @param options - Variations.
 */
export const track = async (
  event: string,
  properties = {},
  systems: SystemType[] = ['keen', 'pendo'],
  options: Options = {},
) => {
  const shouldTrackTo = frequencyChecker(event, systems, options);
  const promises = shouldTrackTo.map(async (system) => {
    return await trackingMap(system, event, properties, options)();
  });

  return Promise.all(promises).catch((err) => {
    // if a tracking call fails:
    const errDetails = `Tracking error for event: ${event} in ${err.message}`;
    if (process.env.NODE_ENV === 'production') throw new Error(errDetails);
  });
};

/**.
 * Set Storage
 *
 * @param system - System to be tracked.
 * @param event - Event type.
 * @param options - Variations.
 */
const setStorage = (system: SystemType, event: string, options: Options) => {
  const { frequency } = options;
  if (frequency) {
    localStorage.setItem(`${system}:${event}`, new Date().toISOString());
  }
};

type KeenEvent = {
  action: string;
  event: string;
  properties: any;
  metadata: any;
};

type PromiseResolvers = {
  resolve: () => void;
  reject: (reason: Error) => void;
};

let keenEvents: KeenEvent[] = [];
let keenEventPromiseResolvers: PromiseResolvers[] = [];

let timer: undefined | ReturnType<typeof setTimeout>;

/**
 * Keen tracker.
 * @param event - Event type.
 * @param properties - Accumulated through the work flow.
 * @param options - Variations.
 */

const trackKeen = async (event: string, properties = {}, options = {}) => {
  // collect metadata
  let metadata = {};
  metadata = {
    platform: window === window.parent ? 'web-app' : 'embed',
    deviceID: await getDeviceID(),
  };

  // check if within an iframe  // how to mock this?
  try {
    if (window !== window.parent) {
      const { hostname } = new URL(document.referrer);
      const url = {
        embed_url: document.referrer,
        embed_domain: hostname,
      };
      metadata = { ...metadata, ...url };
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
  }

  devLog('[keen]', { event, properties, metadata });

  const trackingObj = {
    action: 'track',
    event,
    properties,
    metadata,
  };

  keenEvents.push(trackingObj);

  function debounce() {
    return new Promise<void>((res, rej) => {
      keenEventPromiseResolvers.push({ resolve: res, reject: rej });
      clearTimeout(timer);
      timer = setTimeout(() => {
        const events = [...keenEvents];
        const resolvers = [...keenEventPromiseResolvers];
        keenEvents = [];
        keenEventPromiseResolvers = [];
        axios
          .post('/keen', { events })
          .then(() => {
            if (options) setStorage('keen', event, options);
            // resolve all hanging promises
            resolvers.forEach(({ resolve }) => resolve?.());
          })
          .catch((err) => {
            // events not tracked returned to keenEvents
            keenEvents.push(...events);
            const error = `keen: ${err}`;
            // reject all hanging promises
            resolvers.forEach(({ reject }) => reject?.(new Error(error)));
          });
      }, 500);
    });
  }

  if (keenEvents?.length) await debounce();
};

/**.
 * Waiting Function
 *
 * @param options - Variations.
 */

const waitForPendo = async (options: Options = {}) => {
  const { maxTries = 10, interval = 1000 } = options;
  let numTries = 0;
  const func = () => {
    if ((globalThis as any).pendo || numTries > maxTries) {
      clearInterval(currInterval);
    } else {
      numTries += 1;
    }
  };
  const currInterval = setInterval(func, interval);
};

/**.
 * PendoAnalytics
 *
 * @param event - Event type.
 * @param properties - Accumulated through the work flow.
 * @param options - Variations.
 */

const pendoAnalytics = async (
  event: string,
  properties: Property,
  options: Options,
) => {
  // set platform
  let pendoProperties = { ...properties };
  pendoProperties.platform = window === window.parent ? 'web-app' : 'embed';
  // check if within an iframe
  if (window !== window.parent) {
    const { hostname } = new URL(document.referrer);
    pendoProperties = {
      ...pendoProperties,
      embedURL: document.referrer,
      embedDomain: hostname,
    };
  }

  devLog('[pendo]', { event, properties: pendoProperties });

  await waitForPendo();

  return trackPendo(pendoProperties, options, event);
};

const getExpires = (): Date => {
  const expires = new Date();
  expires.setDate(new Date().getDate() + 365);
  return expires;
};

const generateID = async (): Promise<string | null> => {
  let ID = null;
  try {
    const fp = await FingerprintJS.load();
    const data = await fp.get();
    ID = data.visitorId;

    if (window.Storage) {
      window.localStorage.setItem('deviceFingerprint', ID);
    }

    setCookie('deviceFingerprint', ID, {
      expires: getExpires(),
      sameSite: 'strict',
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log((error as Error).toString());
  }

  return ID;
};

const getFromLS = (): string | null => {
  if (window.Storage) {
    const ID = window.localStorage.getItem('deviceFingerprint');
    if (ID) {
      setCookie('deviceFingerprint', ID, {
        expires: getExpires(),
        sameSite: 'strict',
      });
    }
    return ID;
  }
  return null;
};

/**
 * Retrieves the user's device fingerprint ID from cookies, localStorage, or generates a new one if not present.
 * @returns {Promise<string | null>} The device fingerprint ID.
 */
export const getDeviceID = async () => {
  return (
    getCookie('deviceFingerprint')?.toString() ||
    getFromLS() ||
    (await generateID())
  );
};

/**.
 * Tracking Pendo
 *
 * @param pendoProperties - Accumulated through the work flow.
 * @param event - Event type.
 * @param options - Variations.
 * @throws Error - if tracker never placed on pendo
 */

const trackPendo = (pendoProperties: any, options: Options, event: string) => {
  if (options) setStorage('pendo', event, options);
  if ((globalThis as any).pendo?.track) {
    (globalThis as any).pendo?.track(event, pendoProperties);

    return;
  } else {
    if (process.env.NODE_ENV === 'production')
      throw new Error('pendo tracker not found');
  }
};

// GA4 ecommerce events that require user consent before firing
const GA4_ECOMMERCE_EVENTS = [
  'view_item',
  'select_item',
  'add_to_cart',
  'remove_from_cart',
  'begin_checkout',
  'add_shipping_info',
  'add_payment_info',
  'purchase',
  'view_item_list',
  'refund',
] as const;

/**
 * Get the GTM dataLayer array safely (SSR-compatible).
 * Always returns the current window.dataLayer to avoid stale references.
 */
export const getDataLayer = (): unknown[] => {
  if (typeof window === 'undefined') return [];
  return Array.isArray(window.dataLayer) ? window.dataLayer : [];
};

/**
 * Push a payload to the GTM dataLayer and log the event.
 * @param payload - The event payload to push to dataLayer.
 */
export const pushToDataLayer = (payload: any) => {
  const dl = getDataLayer();
  dl.push(payload);
  devLog('[gtm]', { event: payload.event, properties: payload });
};

/**
 * Push events to GTM's dataLayer.
 * GA4 ecommerce events wait for analytics consent before firing.
 */
export const trackGTM = (
  event: string,
  properties: Property,
  options: Options,
) => {
  if (typeof window === 'undefined') return;

  const payload = { event, ...properties };

  // GA4 ecommerce events wait for consent, all others fire immediately
  const isGA4Event = GA4_ECOMMERCE_EVENTS.some(
    (e) => e.toLowerCase() === (event || '').toLowerCase(),
  );

  devLog('[trackGTM] Event:', { event, isGA4: isGA4Event });

  // Bypass consent checks in development if env variable is set
  // const bypassConsent = process.env.NEXT_PUBLIC_BYPASS_CONSENT === 'true';
  const bypassConsent = false;

  if (isGA4Event && !bypassConsent) {
    // CRITICAL: Double-check consent synchronously to prevent race conditions
    // This catches cases where consent arrives between queue check and polling start
    devLog('[trackGTM] GA4 event detected, checking consent...');
    if (hasGTMConsentUpdate()) {
      devLog('[trackGTM] ✓ Consent exists - pushing immediately:', event);
      pushToDataLayer(payload);
      return;
    }

    // Queue event - it will be flushed when consent is detected
    devLog('[trackGTM] ✗ No consent yet - queueing event:', event);
    queueGA4Event(event, properties);

    // Double-check consent AGAIN after queueing (race condition mitigation)
    if (hasGTMConsentUpdate()) {
      devLog(
        '[trackGTM] ⚠ Consent detected after queueing - will flush on next poll',
      );
      // Don't start polling, consent already exists
      // The next polling check will flush the queue
      return;
    }

    const queueLength = getPendingGA4Events().length;
    devLog(`[trackGTM] Queue length: ${queueLength}`);

    // Start waiting for consent (only once)
    if (queueLength === 1) {
      devLog('[trackGTM] First event in queue - starting consent polling');
      waitForOneTrustReady();
    } else {
      devLog('[trackGTM] Polling already active, waiting for consent...');
    }
  } else {
    // Non-GA4 events fire immediately
    pushToDataLayer(payload);
  }
};

const trackInternal = async (
  event: string,
  properties: InternalProperties,
  options: Options,
) => {
  devLog('[internal]', { event, properties });

  try {
    await axios.post('/v0/track', { event, properties });
    if (options) setStorage('internal', event, options);
  } catch (err) {
    const error = `internal: ${err}`;
    if (process.env.NODE_ENV === 'production') throw new Error(error);
  }
};
